import { loadStripe } from "@stripe/stripe-js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/sharp-regular-svg-icons";
import { useSearchParams } from "react-router-dom";
import { Modal, Table } from "@mantine/core";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { AppealPackage } from "../__generated__/graphql";
import { gql } from "@/__generated__";
import InlineBanner from "../components/shared/InlineBanner";
import Navbar from "@/components/layout/Navbar";
import PurchaseAppealPackages from "@/components/PurchaseAppealPackages";
import PageLoader from "../components/PageLoader";

import styles from "../styles/components/sponsor-appeals.module.scss";

export const CREATE_APPEAL_PACKAGE_CHECKOUT_SESSION = gql(`
  mutation CreateAppealPackageCheckoutSession($appealPackage: AppealPackage!) {
    createAppealPackageCheckoutSession(appealPackage: $appealPackage)
  }
`);

export const GET_PROVIDER_INFORMATION = gql(`
  query ProviderInfo {
    provider {
      providerId
      providerName
      users {
          email
      }
      sponsoredAppeals {
          totalRemaining
      }
      purchases {
          providerUserId
          appealPackageName
          appealQuantity
          amountSubtotal
          amountTax
          amountTotal
          paymentTimestamp
      }
    }
  }
`);

export const GET_CHECKOUT_SESSION_STATUS = gql(`
  query ProviderCheckoutSessionStatus($sessionId: ID!) {
    providerCheckoutSessionStatus(sessionId: $sessionId) {
      complete
    }
  }
`);

const THIRTY_SECONDS = 30000;
const TEN_SECONDS = 10000;

function SponsorAppeals() {
  const [purchaseMade, setPurchaseMade] = useState<boolean>(false);
  const [creditsRemaining, setCreditsRemaining] = useState<number>(0);
  const [buyMoreAppeals, setBuyMoreAppeals] = useState<boolean>(false);
  const [pollingTimedOut, setPollingTimedOut] = useState<boolean>(false);
  const [successBannerDismissed, setSuccessBannerDismissed] =
    useState<boolean>(false);
  const [takingAwhile, setTakingAwhile] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const [loading10Pack, setLoading10Pack] = useState<boolean>(false);
  const [loading30Pack, setLoading30Pack] = useState<boolean>(false);
  const [loading100Pack, setLoading100Pack] = useState<boolean>(false);
  const packPurchasedParam = searchParams.get("successfulPurchase");
  const checkoutSessionId = searchParams.get("sessionId");
  const displayPurchaseModal = searchParams.get("displayPurchaseModal");

  useEffect(() => {
    if (displayPurchaseModal) {
      setBuyMoreAppeals(true);
    }
  }, []);
  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch: refetchProviderInformation,
  } = useQuery(GET_PROVIDER_INFORMATION, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (queryData) {
      if (!queryData.provider) {
        throw new Error("Provider not found.");
      }
      if (
        queryData.provider.purchases &&
        queryData.provider.purchases.length > 0
      ) {
        setPurchaseMade(true);
        setCreditsRemaining(
          queryData.provider.sponsoredAppeals?.totalRemaining ?? 0,
        );
      } else {
        setPurchaseMade(false);
        setCreditsRemaining(0);
      }
    }
  }, [queryData]);

  const [createAppealPackageCheckoutSession, { error }] = useMutation(
    CREATE_APPEAL_PACKAGE_CHECKOUT_SESSION,
  );

  const [
    executeCheckoutSessionQuery,
    {
      startPolling: startPollingForCheckoutSessionCompletion,
      stopPolling: stopPollingForCheckoutSessionCompletion,
      error: checkoutSessionPollingError,
      data: checkoutSessionPollingData,
    },
  ] = useLazyQuery(GET_CHECKOUT_SESSION_STATUS, {
    pollInterval: 1000,
    fetchPolicy: "no-cache",
  });
  const checkoutSuccessful =
    checkoutSessionPollingData?.providerCheckoutSessionStatus?.complete;

  const checkoutSessionPollingDataRef = useRef(checkoutSessionPollingData);
  useEffect(() => {
    checkoutSessionPollingDataRef.current = checkoutSessionPollingData;
  }, [checkoutSessionPollingData]);

  useEffect(() => {
    if (checkoutSessionId) {
      executeCheckoutSessionQuery({
        variables: {
          // Adding the variables here (instead of initialization) to avoid auto-querying when state changes. See this thread: https://github.com/apollographql/apollo-client/issues/5912#issuecomment-2145632697
          sessionId: checkoutSessionId as string,
        },
      });
      startPollingForCheckoutSessionCompletion(2000);

      setTimeout(() => {
        if (
          !checkoutSessionPollingDataRef?.current?.providerCheckoutSessionStatus
            ?.complete
        ) {
          setPollingTimedOut(true);
          stopPollingForCheckoutSessionCompletion();
        }
      }, THIRTY_SECONDS);

      setTimeout(() => {
        if (
          !checkoutSessionPollingDataRef?.current?.providerCheckoutSessionStatus
            ?.complete
        ) {
          setTakingAwhile(true);
        }
      }, TEN_SECONDS);
    }
  }, [
    checkoutSessionId,
    startPollingForCheckoutSessionCompletion,
    checkoutSessionPollingData?.providerCheckoutSessionStatus?.complete,
  ]);

  useEffect(() => {
    if (checkoutSessionPollingData?.providerCheckoutSessionStatus?.complete) {
      stopPollingForCheckoutSessionCompletion();
      setPollingTimedOut(false);
      setTakingAwhile(false);

      refetchProviderInformation();
    }
  }, [
    stopPollingForCheckoutSessionCompletion,
    checkoutSessionPollingData?.providerCheckoutSessionStatus?.complete,
  ]);

  const purchasePackage = async (appealPackage: AppealPackage) => {
    if (appealPackage === AppealPackage.TenPack) {
      setLoading10Pack(true);
    } else if (appealPackage === AppealPackage.ThirtyPack) {
      setLoading30Pack(true);
    } else {
      setLoading100Pack(true);
    }
    const { data } = await createAppealPackageCheckoutSession({
      variables: { appealPackage },
    });
    if (data?.createAppealPackageCheckoutSession) {
      const stripe = await loadStripe(
        import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
      );

      stripe?.redirectToCheckout({
        sessionId: data.createAppealPackageCheckoutSession,
      });
    }

    setLoading10Pack(false);
    setLoading30Pack(false);
    setLoading100Pack(false);
  };

  const displayPackageName = (appealPackage: string) => {
    switch (appealPackage) {
      case AppealPackage.TenPack:
        return "10-Pack";
      case AppealPackage.ThirtyPack:
        return "30-Pack";
      case AppealPackage.OneHundredPack:
        return "100-Pack";
      default:
        return "";
    }
  };

  const displayPurchasePrice = (price: number) => `$${price / 100}`;

  const determineCountStyling = (remainingCredits: number) => {
    if (remainingCredits > 3) {
      return styles.remainingCount;
    }

    if (remainingCredits === 0) {
      return styles.remainingCountDanger;
    }

    return styles.remainingCountWarning;
  };

  return (
    <Navbar titleText="Sponsor appeals">
      <div className={styles.pageRoot}>
        {queryLoading && <PageLoader />}

        {queryError && (
          <div className={styles.banner}>
            <InlineBanner
              type="error"
              message={queryError.message}
              key={queryError.message}
              dismissable
            />
          </div>
        )}

        {checkoutSessionPollingError && (
          <div className={styles.banner}>
            <InlineBanner
              type="error"
              message={checkoutSessionPollingError.message}
              key={checkoutSessionPollingError.message}
              dismissable
            />
          </div>
        )}

        {!queryLoading && !queryError && (
          <>
            {error && (
              <div className={styles.banner}>
                <InlineBanner
                  type="error"
                  message={error.message}
                  key={error.message}
                  dismissable
                />
              </div>
            )}
            {checkoutSuccessful && !successBannerDismissed && (
              <div className={styles.banner}>
                <InlineBanner
                  type="success"
                  message={`Success! ${packPurchasedParam}-Pack of Appeals has been added to your account`}
                  key="successfulPurchase"
                  dismissable
                  onDismiss={() => setSuccessBannerDismissed(true)}
                />
              </div>
            )}
            {takingAwhile && !pollingTimedOut && (
              <div className={styles.banner}>
                <InlineBanner
                  type="information"
                  message="Still adding credits to your account..."
                  key="stillWorkingPolling"
                  dismissable
                />
              </div>
            )}
            {pollingTimedOut && (
              <div className={styles.banner}>
                <InlineBanner
                  type="error"
                  message="Something may have failed. We will reach out to you with info. In the meantime, you can try refreshing the page."
                  key="incompletePolling"
                  dismissable
                />
              </div>
            )}
            {purchaseMade && queryData?.provider ? (
              <div className={styles.displayReferrals}>
                <div className={styles.remainingReferrals}>
                  <div className={determineCountStyling(creditsRemaining)}>
                    <img
                      className={styles.appealRemaingingLogo}
                      src="/images/logo/logo_black.svg"
                      alt="Claimable"
                    />
                    <div className={styles.remainingNumber}>
                      {creditsRemaining}
                    </div>
                    <div className={styles.appealRemainingText}>
                      {`Appeal${creditsRemaining === 1 ? "" : "s"} Remaining`}
                    </div>
                  </div>
                  <div className={styles.sponsorAppealsText}>
                    <div className={styles.sponsorAppealsHeader}>
                      {"Sponsor Patient Appeals"}
                    </div>
                    <div className={styles.sponsorAppealsBody}>
                      {
                        "Sponsor patient appeals effortlessly: Purchase a pack of appeals to cover your patients’ costs. You’re only charged for submitted appeals."
                      }
                    </div>
                    <button
                      type="button"
                      className={styles.sponsorAppealsButton}
                      onClick={() => {
                        setBuyMoreAppeals(true);
                      }}
                    >
                      {"Sponsor appeals"}
                    </button>
                  </div>
                </div>

                <div className={styles.purchaseHistory}>
                  <div className={styles.titleSection}>
                    <div className={styles.tableTitle}>{"Invoice History"}</div>
                  </div>
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>{"Appeal Credits"}</Table.Th>
                        <Table.Th>{"Total"}</Table.Th>
                        <Table.Th>{"Date"}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {queryData?.provider.purchases?.map((purchase) => (
                        <Table.Tr key={purchase.paymentTimestamp}>
                          <Table.Td>
                            {displayPackageName(purchase.appealPackageName)}
                          </Table.Td>
                          <Table.Td>
                            {displayPurchasePrice(purchase.amountTotal)}
                          </Table.Td>
                          <Table.Td>
                            {format(
                              new Date(purchase.paymentTimestamp),
                              "MM/dd/yy",
                            )}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className={styles.purchaseReferrals}>
                <div className={styles.topSection}>
                  <div className={styles.pageText}>
                    <h4 className={styles.subheader}>
                      {"Choose the best option for your practice"}
                    </h4>
                    <p className={styles.text}>
                      {
                        "Refer patients for free, or purchase appeal credits to cover their costs. Credits are only used for submitted appeals."
                      }
                    </p>
                    <p className={styles.text}>
                      <a
                        href="mailto:providers@getclaimable.com"
                        className={styles.link}
                      >
                        {"Need something different? Let’s talk."}
                      </a>
                    </p>
                  </div>
                  <div className={styles.quoteBlock}>
                    <div className={styles.quoteContent}>
                      <img
                        className={styles.quoteImage}
                        src="/images/product_illustrations/julie.png"
                        alt="Claimable"
                      />
                      <div className={styles.quoteTextBlock}>
                        <div className={styles.quoteText}>
                          {`“Claimable's AI-driven approach is a game changer for patients who have been unfairly denied care”`}
                        </div>
                        <div className={styles.quoteRef}>
                          <strong>{"Julie Baak,"}</strong>&nbsp;
                          {
                            "Practice Manager & Patient Advocate Arthritis Center, Bridgeton, MO"
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.bottomSection}>
                  <div className={styles.plan} aria-disabled>
                    <div className={styles.planHeader}>
                      <span className={styles.planCategory}>{"Free plan"}</span>
                      <span className={styles.planType}>
                        {"Basic Referrals"}
                      </span>
                      <span className={styles.whoPays}>
                        {"Patients pay for appeals"}
                      </span>
                    </div>
                    <div className={styles.priceSection}>
                      <span className={styles.costDesc}>{"Patient cost"}</span>
                      <span className={styles.pricePerAppeal}>{"$50"}</span>
                      <span className={styles.perAppealText}>
                        {"PER APPEAL SENT*"}
                      </span>
                      <button type="button" className={styles.planButton}>
                        {"Current Plan"}
                      </button>
                    </div>
                    <div className={styles.planDescription}>
                      <span className={styles.howItWorks}>
                        {"How it works:"}
                      </span>
                      <div className={styles.points}>
                        <div className={styles.singlePoint}>
                          <FontAwesomeIcon icon={faCheck} />
                          <span className={styles.pointText}>
                            {"Refer patients for free"}
                          </span>
                        </div>
                        <div className={styles.singlePoint}>
                          <FontAwesomeIcon icon={faCheck} />
                          <span className={styles.pointText}>
                            {
                              "Patients pay $50 per appeal on average ($39.95 plus shipping)"
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={styles.averageCostDiv}>
                      <span className={styles.averageCostText}>
                        {"*Average cost after calculating shipping"}
                      </span>
                    </div>
                    <div className={styles.totalCostSection}>
                      <div className={styles.footerText}>
                        <span className={styles.totalCostPrice}>{"Free"}</span>
                      </div>
                    </div>
                  </div>
                  <PurchaseAppealPackages
                    onSubmit={purchasePackage}
                    loading10Pack={loading10Pack}
                    loading30Pack={loading30Pack}
                    loading100Pack={loading100Pack}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <Modal
        opened={!!buyMoreAppeals}
        onClose={async () => {
          setBuyMoreAppeals(false);
        }}
        radius={16}
        centered
        size="xl"
      >
        <div className={styles.modalHeading}>
          {"Sponsor appeals for patients"}
        </div>
        <div className={styles.modalSection}>
          <PurchaseAppealPackages
            onSubmit={purchasePackage}
            loading10Pack={loading10Pack}
            loading30Pack={loading30Pack}
            loading100Pack={loading100Pack}
          />
        </div>
      </Modal>
    </Navbar>
  );
}

export default SponsorAppeals;
